import type { ComponentType } from 'react'

interface ActionObject {
  label: string
  icon?: ComponentType<{ size?: number; style?: React.CSSProperties }>
  onClick: () => void
}

interface Props {
  icon?: React.ReactNode | ComponentType<{ size?: number; style?: React.CSSProperties }>
  title: string
  description: string
  action?: React.ReactNode | ActionObject
}

function isActionObject(action: unknown): action is ActionObject {
  return typeof action === 'object' && action !== null && 'label' in action && 'onClick' in action
}

function isComponent(icon: unknown): icon is ComponentType<{ size?: number; style?: React.CSSProperties }> {
  return typeof icon === 'function'
}

export function EmptyState({ icon, title, description, action }: Props) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        textAlign: 'center',
        animation: 'in-up 400ms var(--ease) both',
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 'var(--r-l)',
          background: 'var(--bg-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 16,
        }}
      >
        {icon ? (
          isComponent(icon) ? (
            (() => {
              const IconComp = icon
              return <IconComp size={24} style={{ color: 'var(--text-3)' }} />
            })()
          ) : (
            icon
          )
        ) : (
          <svg
            width={24}
            height={24}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: 'var(--text-3)' }}
          >
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
          </svg>
        )}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--text-2)',
          fontFamily: 'var(--font)',
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-3)',
          fontFamily: 'var(--font)',
          marginBottom: action ? 20 : 0,
          maxWidth: 280,
          lineHeight: 1.5,
        }}
      >
        {description}
      </div>
      {isActionObject(action) ? (
        <button
          className="v3-btn v3-btn-primary"
          onClick={action.onClick}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {action.icon && <action.icon size={15} />}
          {action.label}
        </button>
      ) : (
        action
      )}
    </div>
  )
}
